import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";

const empty = {
    title: "",
    hero_image: "",
    short_description: "",
    backstory: "",
    cultural_significance: "",
    origin: "Greece",
    categories: [],
    difficulty: "Easy",
    prep_time_minutes: 10,
    cook_time_minutes: 30,
    servings: 4,
    ingredients: [""],
    instructions: [""],
    tips: [""],
    featured: false,
    episode: "",
    is_free: false,
};

const ALL_CATEGORIES = [
    "Cyprus", "Greece", "Village Recipes", "Seafood",
    "Street Food", "Easter Recipes", "Vegetarian", "Desserts",
    "Date Night",
];

const AdminRecipeForm = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/recipes/${id}`)
            .then((r) => setForm({
                ...empty,
                ...r.data,
                categories: r.data.categories || [],
                ingredients: r.data.ingredients?.length ? r.data.ingredients : [""],
                instructions: r.data.instructions?.length ? r.data.instructions : [""],
                tips: r.data.tips?.length ? r.data.tips : [""],
            }))
            .catch(() => setError("Could not load recipe"))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setListItem = (key, i, v) =>
        setForm((f) => {
            const next = [...f[key]];
            next[i] = v;
            return { ...f, [key]: next };
        });
    const addListItem = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
    const removeListItem = (key, i) =>
        setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

    const toggleCategory = (c) =>
        setForm((f) => ({
            ...f,
            categories: f.categories.includes(c)
                ? f.categories.filter((x) => x !== c)
                : [...f.categories, c],
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        const payload = {
            ...form,
            ingredients: form.ingredients.map((s) => s.trim()).filter(Boolean),
            instructions: form.instructions.map((s) => s.trim()).filter(Boolean),
            tips: form.tips.map((s) => s.trim()).filter(Boolean),
            prep_time_minutes: Number(form.prep_time_minutes) || 0,
            cook_time_minutes: Number(form.cook_time_minutes) || 0,
            servings: Number(form.servings) || 1,
            episode: form.featured && form.episode !== "" && form.episode !== null
                ? Number(form.episode)
                : null,
        };
        try {
            if (isEdit) {
                await api.put(`/recipes/${id}`, payload);
            } else {
                await api.post("/recipes", payload);
            }
            navigate("/admin");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Failed to save recipe");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="py-20 text-center text-hl-muted">Loading...</div>;

    return (
        <div data-testid="admin-recipe-form" className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-24">
            <Link to="/admin" className="inline-flex items-center gap-2 text-hl-muted hover:text-hl-brown text-[11px] uppercase tracking-[0.3em] mb-6">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            <p className="font-script text-2xl text-hl-secondary leading-none">
                {isEdit ? "edit a dish —" : "add a new dish —"}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-hl-brown mt-2 mb-10">
                {isEdit ? "Edit recipe" : "New recipe"}
            </h1>

            {error ? (
                <p data-testid="form-error" className="mb-6 px-4 py-3 rounded-sm bg-hl-secondary/15 text-hl-secondaryDeep border border-hl-secondary/40">
                    {error}
                </p>
            ) : null}

            <form onSubmit={onSubmit} className="space-y-7 bg-hl-surface border border-hl-border rounded-sm p-8 hl-shadow-warm">
                <Field label="Title" required>
                    <input data-testid="form-title" value={form.title} onChange={(e) => set("title", e.target.value)} required className={inputCls} />
                </Field>

                <Field label="Hero image URL" required>
                    <input data-testid="form-hero-image" value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} required placeholder="https://..." className={inputCls} />
                    {form.hero_image ? (
                        <img src={form.hero_image} alt="" className="mt-3 w-full h-44 object-cover rounded-sm border border-hl-border" />
                    ) : null}
                </Field>

                <Field label="Short description" required>
                    <input data-testid="form-short-description" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} required maxLength={200} className={inputCls} />
                </Field>

                <Field label="Backstory (the history of the dish)" required>
                    <textarea data-testid="form-backstory" value={form.backstory} onChange={(e) => set("backstory", e.target.value)} required rows={8} className={`${inputCls} font-serif italic`} />
                </Field>

                <Field label="Cultural significance">
                    <textarea data-testid="form-cultural-significance" value={form.cultural_significance} onChange={(e) => set("cultural_significance", e.target.value)} rows={4} className={inputCls} />
                </Field>

                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Origin">
                        <select data-testid="form-origin" value={form.origin} onChange={(e) => set("origin", e.target.value)} className={inputCls}>
                            <option>Greece</option>
                            <option>Cyprus</option>
                        </select>
                    </Field>
                    <Field label="Difficulty">
                        <select data-testid="form-difficulty" value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className={inputCls}>
                            <option>Easy</option>
                            <option>Medium</option>
                            <option>Hard</option>
                        </select>
                    </Field>
                </div>

                <Field label="Categories">
                    <div className="flex flex-wrap gap-2">
                        {ALL_CATEGORIES.map((c) => (
                            <button
                                type="button"
                                key={c}
                                data-testid={`form-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => toggleCategory(c)}
                                className={`px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] rounded-full border transition-colors ${
                                    form.categories.includes(c)
                                        ? "bg-hl-secondary text-hl-bg border-hl-secondary"
                                        : "border-hl-border text-hl-muted hover:border-hl-secondary"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </Field>

                <div className="grid md:grid-cols-3 gap-5">
                    <Field label="Prep (min)">
                        <input data-testid="form-prep" type="number" min={0} value={form.prep_time_minutes} onChange={(e) => set("prep_time_minutes", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Cook (min)">
                        <input data-testid="form-cook" type="number" min={0} value={form.cook_time_minutes} onChange={(e) => set("cook_time_minutes", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Servings">
                        <input data-testid="form-servings" type="number" min={1} value={form.servings} onChange={(e) => set("servings", e.target.value)} className={inputCls} />
                    </Field>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="form-featured"
                        checked={form.featured}
                        onChange={(e) => set("featured", e.target.checked)}
                        className="w-4 h-4 accent-hl-secondary"
                    />
                    <span className="text-[11px] uppercase tracking-[0.25em] text-hl-muted">
                        Feature in "Taste the Story" & Home hero
                    </span>
                </label>

                {form.featured ? (
                    <Field label='Episode number (for "Taste the Story" — leave blank to hide)'>
                        <input
                            data-testid="form-episode"
                            type="number"
                            min={1}
                            value={form.episode ?? ""}
                            onChange={(e) => set("episode", e.target.value)}
                            placeholder="e.g. 1, 2, 3..."
                            className={`${inputCls} max-w-xs`}
                        />
                        <p className="mt-2 text-xs text-hl-muted/80 italic">
                            Renders as "Ep. 01" badge on Taste-the-Story cards and the recipe hero.
                        </p>
                    </Field>
                ) : null}

                <ListField label="Ingredients" items={form.ingredients} onChange={(i, v) => setListItem("ingredients", i, v)} onAdd={() => addListItem("ingredients")} onRemove={(i) => removeListItem("ingredients", i)} testPrefix="ingredient" placeholder="2 eggplants, sliced 1cm thick" />

                <ListField label="Instructions (step by step)" items={form.instructions} onChange={(i, v) => setListItem("instructions", i, v)} onAdd={() => addListItem("instructions")} onRemove={(i) => removeListItem("instructions", i)} testPrefix="step" multiline placeholder="Describe this step..." />

                <ListField label="Tips from the village" items={form.tips} onChange={(i, v) => setListItem("tips", i, v)} onAdd={() => addListItem("tips")} onRemove={(i) => removeListItem("tips", i)} testPrefix="tip" multiline placeholder="A piece of grandma wisdom..." />

                <div className="flex items-center gap-3 pt-2">
                    <button type="submit" data-testid="form-submit" disabled={saving} className="inline-flex items-center gap-2 px-7 py-3.5 bg-hl-primary text-hl-bg hover:bg-hl-primaryDeep hl-btn-lift disabled:opacity-50 text-[11px] uppercase tracking-[0.3em]">
                        <Save className="w-4 h-4" />
                        {saving ? "Saving..." : isEdit ? "Save changes" : "Create recipe"}
                    </button>
                    <Link to="/admin" className="px-6 py-3 text-hl-muted hover:text-hl-brown text-[11px] uppercase tracking-[0.3em]">
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
};

const inputCls =
    "w-full px-4 py-3 rounded-sm border border-hl-border bg-hl-bg text-hl-text focus:outline-none focus:border-hl-primary";

const Field = ({ label, children, required }) => (
    <div>
        <label className="block text-[11px] uppercase tracking-[0.25em] text-hl-muted mb-2">
            {label}{required ? " *" : ""}
        </label>
        {children}
    </div>
);

const ListField = ({ label, items, onChange, onAdd, onRemove, testPrefix, multiline, placeholder }) => (
    <div>
        <div className="flex items-center justify-between mb-3">
            <label className="text-[11px] uppercase tracking-[0.25em] text-hl-muted">{label}</label>
            <button type="button" data-testid={`${testPrefix}-add`} onClick={onAdd} className="inline-flex items-center gap-1 text-sm text-hl-primary hover:text-hl-primaryDeep">
                <Plus className="w-3.5 h-3.5" /> Add
            </button>
        </div>
        <div className="space-y-3">
            {items.map((val, i) => (
                <div key={i} className="flex items-start gap-2">
                    <span className="mt-2.5 w-7 text-right text-hl-muted/70 text-sm">{i + 1}.</span>
                    {multiline ? (
                        <textarea data-testid={`${testPrefix}-${i}`} rows={2} value={val} onChange={(e) => onChange(i, e.target.value)} placeholder={placeholder} className={`${inputCls} flex-1`} />
                    ) : (
                        <input data-testid={`${testPrefix}-${i}`} value={val} onChange={(e) => onChange(i, e.target.value)} placeholder={placeholder} className={`${inputCls} flex-1`} />
                    )}
                    <button type="button" data-testid={`${testPrefix}-remove-${i}`} onClick={() => onRemove(i)} className="mt-2.5 p-2 rounded-sm text-hl-secondary hover:bg-hl-secondary/10" aria-label="Remove">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            ))}
        </div>
    </div>
);

export default AdminRecipeForm;
